import { getEntitySetValueFields } from "../data/mcaEntities";
import {
  DATA_CURRENCY,
  DATA_ORDER_SERVICE_STATUS,
  DATA_PAY_MODES,
  DATA_PAY_STATUS,
  DATA_PAYMENT_METHOD,
  DATA_PERSONAL_TRADING_ACCOUNT_TYPE,
  DATA_PERSONAL_TRADING_PRODUCT_TYPE,
  DATA_PERSONAL_TRADING_STATUS,
  DATA_PRODUCT_AGETYPES,
} from "../data/options";
import { getMcaEntityFormSchema } from "../components/mca/mcaEntityFormRegistry";
import {
  BOOLEAN_WATCH_VALUE_OPTIONS,
  extractSchemaFieldOptions,
  isKnownBooleanWatchField,
} from "./notificationWatchFieldValueOptions";
import { ORDER_SERVICE_MAPPABLE_FIELDS } from "../data/orderServiceMappableFields";
import { CLIENT_CLASS_FILTER_VALUES } from "./clientClass";

const MCA_CONDITION_TYPES = [
  { value: "$eq", label: "Equals" },
  { value: "$in", label: "In list" },
  { value: "$gt", label: "Greater than" },
  { value: "$gte", label: "Greater or equal" },
  { value: "$lt", label: "Less than" },
  { value: "$lte", label: "Less or equal" },
];

const CONDITION_TYPES_BY_VALUE_TYPE = {
  boolean: ["$eq"],
  enum: ["$eq", "$in"],
  ref: ["$eq", "$in"],
  number: ["$eq", "$gt", "$gte", "$lt", "$lte"],
  date: ["$eq", "$gt", "$gte", "$lt", "$lte"],
  text: ["$eq", "$in"],
};

const LINE_OF_BUSINESS_OPTIONS = [
  { value: "LIFE", label: "Life" },
  { value: "GI", label: "GI" },
  { value: "MPF", label: "MPF" },
  { value: "INV", label: "Investment" },
];

const ORDER_PAYMENT_ENUM_FIELDS = {
  "payment.payMode": DATA_PAY_MODES,
  "payment.payStatus": DATA_PAY_STATUS,
  "payment.currency": DATA_CURRENCY,
  "payment.initialPaymentCurrency": DATA_CURRENCY,
  "payment.prePaymentCurrency": DATA_CURRENCY,
  "payment.payBy": DATA_PAYMENT_METHOD,
  "payment.renewalPayBy": DATA_PAYMENT_METHOD,
  lineOfBusiness: LINE_OF_BUSINESS_OPTIONS,
};

const WORKFLOW_STATUS_OPTIONS = [
  { value: "DFT", label: "Draft" },
  { value: "PENDING", label: "Pending" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
  { value: "CANCELLED", label: "Cancelled" },
];

const STATIC_ENTITY_ENUM_FIELDS = {
  client: {
    lineOfBusiness: LINE_OF_BUSINESS_OPTIONS,
    clientClass: CLIENT_CLASS_FILTER_VALUES,
  },
  order: ORDER_PAYMENT_ENUM_FIELDS,
  debitNote: {
    lineOfBusiness: LINE_OF_BUSINESS_OPTIONS,
  },
  product: {
    ageType: DATA_PRODUCT_AGETYPES,
  },
  productType: {
    "levySettings.ecLevy.formulaType": [
      { value: "full", label: "Full (10.8% for Direct EC)" },
      { value: "parial", label: "Partial (only partial on Gross Premium)" },
    ],
  },
  productDueDiligence: {
    reviewType: [
      { value: "INITIAL", label: "Initial" },
      { value: "RECURRENT", label: "Recurrent" },
    ],
    status: [
      { value: "DFT", label: "Draft" },
      { value: "PENDING", label: "Pending" },
      { value: "IN_REVIEW", label: "In Review" },
      { value: "APPROVED", label: "Approved" },
      { value: "OVERDUE", label: "Overdue" },
      { value: "CANCELLED", label: "Cancelled" },
    ],
  },
  personalTradingDeclaration: {
    status: WORKFLOW_STATUS_OPTIONS,
  },
  personalTradingAccount: {
    accountType: DATA_PERSONAL_TRADING_ACCOUNT_TYPE,
    accountStatus: DATA_PERSONAL_TRADING_STATUS,
    productTypes: DATA_PERSONAL_TRADING_PRODUCT_TYPE,
    status: [
      { value: "DFT", label: "Draft" },
      { value: "APPROVED", label: "Approved" },
    ],
  },
  commissionSchedule: {
    status: [
      { value: "active", label: "Active" },
      { value: "inactive", label: "Inactive" },
    ],
  },
  commissionRelease: {
    status: [
      { value: "DFT", label: "Draft" },
      { value: "PENDING", label: "Pending" },
      { value: "RELEASED", label: "Released" },
      { value: "CANCELLED", label: "Cancelled" },
    ],
  },
  clientTransfer: {
    status: [
      { value: "DFT", label: "Draft" },
      { value: "PENDING", label: "Pending" },
      { value: "APPLIED", label: "Applied" },
      { value: "CANCELLED", label: "Cancelled" },
    ],
  },
  orderService: {
    status: DATA_ORDER_SERVICE_STATUS,
  },
};

const USER_REF_SEARCH_FIELDS = ["name", "staffId", "cname", "email", "aliasName"];

const DEFAULT_USER_REF_CONFIG = {
  apiEntity: "user",
  valueField: "_id",
  labelField: "name",
  preQuery: { status: "ACTIVE" },
  searchFields: USER_REF_SEARCH_FIELDS,
  sort: { by: "name", order: "asc" },
  pageSize: 20,
  labelRender: (data) =>
    `${data.name}${data.aliasName ? `, ${data.aliasName}` : ""} (${data.staffId ?? " -- "})`,
};

const SINGLE_USER_REF_FIELDS = new Set([
  "handlerBy",
  "handlerBy2",
  "signedBy",
  "signedBy2",
  "createdBy",
  "maker",
  "consultant",
  "fromConsultant",
  "toConsultant",
]);

const ORDER_CONSULTANTS_REF_CONFIG = {
  apiEntity: "user",
  valueField: "_id",
  labelField: "name",
  isArrayRef: true,
  preQuery: {
    $and: [{ isConsultant: true }, { status: "ACTIVE" }],
  },
  searchFields: ["aliasName", "name", "staffId", "cname", "email", "phone"],
  pageSize: 2000,
  labelRender: (data) =>
    `[${data.staffId ?? " -- "}] ${data.name}${
      data.aliasName ? `, ${data.aliasName}` : ""
    }${data.cname ? ` ${data.cname}` : ""}`,
};

const STATIC_REF_FIELD_CONFIG = {
  user: {
    departmentId: {
      apiEntity: "department",
      valueField: "_id",
      labelField: "name",
      searchFields: ["name", "code"],
      preQuery: { active: true },
      pageSize: 100,
    },
  },
  expenseClaim: {
    expenseType: {
      apiEntity: "expenseType",
      valueField: "_id",
      labelField: "name",
      searchFields: ["name", "code"],
      preQuery: { active: true },
      pageSize: 50,
    },
  },
  orderService: {
    serviceType: {
      apiEntity: "orderServiceType",
      valueField: "_id",
      labelField: "name",
      searchFields: ["name", "code", "cname"],
      pageSize: 20,
      labelRender: (data) => `${data.name} ${data.cname ?? ""}`.trim(),
    },
    orderId: {
      apiEntity: "order",
      valueField: "_id",
      labelField: "code",
      searchFields: ["code", "policyNumber"],
      pageSize: 20,
    },
  },
  order: {
    consultants: ORDER_CONSULTANTS_REF_CONFIG,
    client: {
      apiEntity: "client",
      valueField: "_id",
      labelField: "name",
      searchFields: ["name", "cname", "code", "id"],
      pageSize: 20,
    },
    product: {
      apiEntity: "product",
      valueField: "_id",
      labelField: "name",
      searchFields: ["name", "code", "cname"],
      pageSize: 20,
    },
    productType: {
      apiEntity: "productType",
      valueField: "_id",
      labelField: "name",
      searchFields: ["name", "code", "cname"],
      pageSize: 20,
    },
    productCategory: {
      apiEntity: "productCategory",
      valueField: "_id",
      labelField: "name",
      searchFields: ["name", "code", "cname"],
      pageSize: 20,
      labelRender: (data) =>
        `${data.name ?? ""}${data.cname ? ` ${data.cname}` : ""}`.trim(),
    },
    provider: {
      apiEntity: "provider",
      valueField: "_id",
      labelField: "name",
      searchFields: ["name", "code", "cname", "shortName"],
      pageSize: 20,
    },
    debitNote: {
      apiEntity: "debitNote",
      valueField: "_id",
      labelField: "code",
      searchFields: ["code"],
      pageSize: 20,
    },
  },
};

const NUMERIC_FIELD_PATHS = new Set(
  ORDER_SERVICE_MAPPABLE_FIELDS
    .filter((field) =>
      (field.compatibleFieldTypes ?? []).some((type) =>
        /^(INTEGER|DECIMAL)/.test(type)
      )
    )
    .map((field) => field.path)
);

[
  "payment.premium",
  "payment.sumInsured",
  "payment.yearPremium",
  "payment.initialPayment",
  "payment.prePayment",
  "payment.payCount",
  "payment.paymentTerm",
  "payment.paymentTermUntilAge",
  "payment.policyTerm",
  "payment.yearRate",
  "payment.actualYearRate",
  "payment.levy",
  "totalAmount",
  "discountAmount",
  "costTotal",
  "insuredAge",
  "renewalCount",
  "totalWithdrawalAmount",
  "settlement.amount",
  "payment.adjustmentAmount",
  "payment.totalByCredit",
  "amount",
  "amountHkd",
  "amountPerAttendee",
].forEach((path) => NUMERIC_FIELD_PATHS.add(path));

const DATE_FIELD_PATHS = new Set([
  "signedDate",
  "receivedDate",
  "submissionDate",
  "policyCommenceDate",
  "policyIssueDate",
  "policyAckSignDate",
  "policyCoolingOffDate",
  "policyDeliverDate",
  "fnaSignDate",
  "lapsedSurrenderDate",
  "transferOutDate",
  "transferInEffectiveDate",
  "reinstateDate",
  "aipAt",
  "birthday",
  "idDocExpiryDate",
  "employmentDate",
  "accountCloseDate",
  "deadline",
  "startDate",
  "declarationDate",
  "completedAt",
  "releasedAt",
  "appliedAt",
  "holdCover.date",
  "period.start",
  "period.end",
  "settlement.settleDate",
  "payment.maturityDate",
  "payment.preliminaryDate",
  "payment.payUpDate",
  "date",
  "confirmedAt",
  "settledAt",
  "createdAt",
]);

const getComponentName = (component) => {
  if (!component) return "";
  if (typeof component === "string") return component;
  return component.displayName || component.name || "";
};

const isRemoteSelectComponent = (component) => {
  if (!component) return false;
  if (typeof component === "string" && /RemoteSelect/i.test(component)) {
    return true;
  }
  if (/RemoteSelect/i.test(getComponentName(component))) return true;
  const render = component.render ?? component.type?.render;
  if (render && /RemoteSelect/i.test(render.name || "")) return true;
  return false;
};

const isDatePickerComponent = (component) =>
  /DatePicker/i.test(getComponentName(component));

const isNumberInputComponent = (component) =>
  /NumberInput/i.test(getComponentName(component));

const findSchemaField = (schema, fieldPath) =>
  (schema?.layout?.fields ?? []).find((field) => field?.name === fieldPath);

const isDateFieldPath = (fieldPath) => {
  if (!fieldPath) return false;
  if (DATE_FIELD_PATHS.has(fieldPath)) return true;
  if (/\.date$/i.test(fieldPath)) return true;
  if (/Date$/.test(fieldPath)) return true;
  if (/At$/.test(fieldPath)) return true;
  return false;
};

const isNumericFieldPath = (fieldPath) => NUMERIC_FIELD_PATHS.has(fieldPath);

const buildRefMeta = (refConfig) => ({
  valueType: "ref",
  options: null,
  refConfig,
});

const extractSchemaRefConfig = (schema, fieldPath) => {
  const schemaField = findSchemaField(schema, fieldPath);
  if (!schemaField || !isRemoteSelectComponent(schemaField.component)) {
    return null;
  }
  const props = schemaField.props ?? {};
  if (!props.apiEntity) return null;
  return {
    apiEntity: props.apiEntity,
    valueField: props.valueField ?? "_id",
    labelField: props.labelField ?? "name",
    labelRender: props.labelRender,
    preQuery: props.preQuery,
    searchFields: props.searchFields ?? ["code", "name"],
    sort: props.sort,
    pageSize: props.pageSize ?? 20,
    isArrayRef: props.multiple === true,
  };
};

const resolveStaticRefConfig = (entityKey, fieldPath) => {
  const entityConfig = STATIC_REF_FIELD_CONFIG[entityKey]?.[fieldPath];
  if (entityConfig) return { ...entityConfig };

  if (SINGLE_USER_REF_FIELDS.has(fieldPath)) {
    return { ...DEFAULT_USER_REF_CONFIG };
  }

  return null;
};

export const getMcaWatchFieldMeta = (entityKey, fieldPath) => {
  const emptyMeta = { valueType: "text", options: null, refConfig: null };

  if (!fieldPath) {
    return emptyMeta;
  }

  if (isKnownBooleanWatchField(entityKey, fieldPath)) {
    return { valueType: "boolean", options: BOOLEAN_WATCH_VALUE_OPTIONS, refConfig: null };
  }

  const setValueFields = getEntitySetValueFields(entityKey);
  const setValueDef = setValueFields.find((item) => item.field === fieldPath);
  if (setValueDef?.options?.length) {
    return { valueType: "enum", options: setValueDef.options, refConfig: null };
  }

  const staticEnum =
    STATIC_ENTITY_ENUM_FIELDS[entityKey]?.[fieldPath] ?? null;
  if (staticEnum?.length) {
    return { valueType: "enum", options: staticEnum, refConfig: null };
  }

  const schema = entityKey ? getMcaEntityFormSchema(entityKey, (key) => key) : null;
  const schemaRefConfig = extractSchemaRefConfig(schema, fieldPath);
  if (schemaRefConfig) {
    return buildRefMeta(schemaRefConfig);
  }

  const staticRefConfig = resolveStaticRefConfig(entityKey, fieldPath);
  if (staticRefConfig) {
    return buildRefMeta(staticRefConfig);
  }

  const schemaField = findSchemaField(schema, fieldPath);

  if (schemaField && isDatePickerComponent(schemaField.component)) {
    return { valueType: "date", options: null, refConfig: null };
  }

  if (schemaField && isNumberInputComponent(schemaField.component)) {
    return { valueType: "number", options: null, refConfig: null };
  }

  const schemaOptions = extractSchemaFieldOptions(schema, fieldPath);
  if (schemaOptions?.length) {
    const isBoolean =
      schemaOptions.length === BOOLEAN_WATCH_VALUE_OPTIONS.length &&
      BOOLEAN_WATCH_VALUE_OPTIONS.every((option) =>
        schemaOptions.some(
          (candidate) =>
            candidate.value === option.value && candidate.label === option.label
        )
      );
    if (isBoolean) {
      return { valueType: "boolean", options: schemaOptions, refConfig: null };
    }
    return { valueType: "enum", options: schemaOptions, refConfig: null };
  }

  if (isDateFieldPath(fieldPath)) {
    return { valueType: "date", options: null, refConfig: null };
  }

  if (isNumericFieldPath(fieldPath)) {
    return { valueType: "number", options: null, refConfig: null };
  }

  return emptyMeta;
};

export const getMcaConditionTypesForField = (meta) => {
  const allowed = CONDITION_TYPES_BY_VALUE_TYPE[meta?.valueType] ?? CONDITION_TYPES_BY_VALUE_TYPE.text;
  return MCA_CONDITION_TYPES.filter((type) => allowed.includes(type.value));
};

export const isMcaConditionTypeAllowed = (meta, conditionType) => {
  if (!conditionType) return true;
  return getMcaConditionTypesForField(meta).some((type) => type.value === conditionType);
};
